<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { createEventDispatcher } from 'svelte';
	// Constants
	import { BonusLimits } from '@/Constants/Promotions.js';
	// Components
	import Datepicker from '@/Components/Datepicker.svelte';
	import Modal from '@/Components/Modal.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Switch from '@/Components/Switch.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import Prompt from '@/Components/Prompt.svelte';
	// Services
	import { notify } from '@/Services/Notify';
	import { paginationBuilder } from '@/Services/Helpers';
	import ApiService from '@/Services/ApiService.js';
	// Stores
	import PromotionStore from '@/Stores/PromotionStore';
	import PromotionUserStore from '@/Stores/PromotionUserStore';
	import ACLStore from '@/Stores/ACLStore.js';

	export let promotion;
	export let open;

	const dispatch = createEventDispatcher();

	let loading = false;
	let sending = false;
	let closing = false;
	let errors;
	let userList;
	let page = 1;
	let checkValue = false;

	let activeDate = dayjs(promotion.published_at ?? undefined).format('YYYY-MM-DD');
	let activeTime = dayjs(promotion.published_at ?? undefined).format('HH:mm');
	let expireDate = dayjs(promotion.expired_at ?? undefined).format('YYYY-MM-DD');
	let expireTime = dayjs(promotion.expired_at ?? undefined).format('HH:mm');

	let activeToggle = !!promotion.published_at;
	let expireToggle = !!promotion.expired_at;

	$: activeStart = dayjs(activeDate + ' ' + activeTime + ':00');
	$: expireStart = dayjs(expireDate + ' ' + expireTime + ':00');
	$: activeStart && expireStart && updateDateTimes();
	$: links = $PromotionUserStore.count() && paginationBuilder(PromotionUserStore.getPagination()); //Used for pagination component

	function getUsersForPromotion() {
		if (!loading) {
			PromotionUserStore.getUsersForPromotion(promotion, { size: 20, number: page }).then(() => {
				loading = false;
			});
		}
	}

	function changePage(e) {
		if (e) {
			page = e.detail.page;
			getUsersForPromotion();
		}
	}

	function updateDateTimes() {
		promotion.published_at = dayjs(activeStart).format('YYYY-MM-DDTHH:mm:00Z');
		promotion.expired_at = dayjs(expireStart).format('YYYY-MM-DDTHH:mm:00Z');
	}

	function updatePromotion() {
		sending = true;
		promotion
			.update({
				name: promotion.name,
				title: promotion.title,
				description: promotion.description,
				published_at: promotion.published_at,
				expired_at: promotion.expired_at
			})
			.then(() => {
				notify({
					type: 'success',
					title: 'Promotion updated',
					message: 'Promotion successfully updated!',
					timeout: 3
				});
				dispatch('updated');
				open = false;
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem updating promotion',
					timeout: 3
				});
			})
			.finally(() => {
				sending = true;
			});
	}

	function checkBonusAmountConfirmation() {
		if (userList) {
			sending = true;
			if (promotion.rules.max_bonus_amount > 999) {
				checkValue = true;
			} else {
				uploadUsersCSV();
			}
		}
	}

	function uploadUsersCSV() {
		sending = true;
		if (userList) {
			PromotionStore.addUsersToPromotion(promotion, userList)
				.then(() => {
					notify({
						type: 'success',
						title: 'Users Added',
						message: 'Users successfully added to promotion!',
						timeout: 3
					});
					dispatch('updated');
					open = false;
				})
				.catch((e) => {
					errors = e;
					notify({
						type: 'danger',
						title: 'Error!',
						message: (e.error && e.error[0]) || 'Problem adding users to promotion',
						timeout: 3
					});
				})
				.finally(() => {
					sending = true;
				});
		}
	}

	function closePromotion() {
		sending = true;
		closing = true;
		PromotionStore.closePromotion(promotion.id)
			.then(() => {
				notify({
					type: 'success',
					title: 'Promotion closed',
					message: 'Promotion successfully closed!',
					timeout: 3
				});
				dispatch('updated');
				open = false;
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem closing promotion',
					timeout: 3
				});
			})
			.finally(() => {
				sending = true;
				closing = true;
			});
	}

	function fileChange(e) {
		userList = e.target.files[0];
	}

	async function downloadUserList() {
		ApiService.downloadFile(
			`${promotion.resourceUrl()}${promotion.id}/users/export`,
			`${promotion.id} ${promotion.code} users - ${dayjs().format('YYYYMMDDHHmmss')}`
		);
	}

	getUsersForPromotion();
</script>

{#if checkValue}
	<Prompt
		bind:open={checkValue}
		title="Confirm promotion value"
		message="The bonus allocation seems high, Are you sure you want to go ahead?"
		onConfirm={() => uploadUsersCSV()}
		on:close={() => {
			sending = false;
		}}
	/>
{/if}

{#if !!open}
	<Modal on:close={() => (open = false)} className="md:w-2/3 lg:w-1/2 mx-2 md:mx-auto">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">
			Update promotion - {promotion.name}
		</h2>
		<div class="flex flex-row justify-end items-center mt-4 mb-1 px-4">
			<LoadingButton
				on:click={() => closePromotion()}
				loading={sending}
				className="my-4 btn {!($ACLStore && ACLStore.granted('promotions.update'))
					? 'btn-primary-action-disabled'
					: 'btn-primary'}"
				disabled={!($ACLStore && ACLStore.granted('promotions.update'))}
			>
				{closing ? 'Closing Promotion' : 'Close This Promotion'}
			</LoadingButton>
		</div>
		<div class="flex flex-col w-full px-4 pt-2 text-left space-y-4">
			<div class="flex flex-row space-x-4">
				<TextInput
					label="Promotion Name"
					name="name"
					className="w-1/3"
					placeholder="Please enter the promotion name"
					errors={errors && errors.name}
					bind:value={promotion.name}
				/>
				<TextInput
					label="Promotion Title"
					name="title"
					className="w-1/3"
					placeholder="Please enter the promotion title"
					errors={errors && errors.title}
					bind:value={promotion.title}
				/>
				<TextInput
					label="Promotion Code"
					name="code"
					className="w-1/3"
					placeholder="Offer Code"
					disabled
					errors={errors && errors.code}
					bind:value={promotion.code}
				/>
			</div>
			<TextInput
				label="Promotion Description"
				name="description"
				className="w-full"
				placeholder="Please enter the promotion description"
				errors={errors && errors.description}
				bind:value={promotion.description}
			/>
			<div class="flex flex-row space-x-4">
				<SelectInput
					className="w-1/3"
					label="Select Template Type"
					name="template_type"
					disabled
					errors={errors && errors.template_type}
					required={true}
					bind:value={promotion.rules.percentage}
				>
					<option selected disabled>Select template type...</option>
					<option value="10">10%</option>
					<option value="20">20%</option>
					<option value="25">25%</option>
					<option value="30">30%</option>
					<option value="40">40%</option>
					<option value="50">50%</option>
					<option value="60">60%</option>
					<option value="75">75%</option>
					<option value="100">100%</option>
					<option value="150">150%</option>
					<option value="200">200%</option>
				</SelectInput>
				<SelectInput
					className="w-1/3"
					label="Max Bonus Amount"
					name="max_bonus"
					disabled
					errors={errors && errors.max_bonus}
					required={true}
					bind:value={promotion.rules.max_bonus_amount}
				>
					<option value={null} selected disabled>Select Amount...</option>
					{#each BonusLimits as bonusLimit (bonusLimit.value)}
						<option value={bonusLimit.value}>{bonusLimit.label}</option>
					{/each}
				</SelectInput>
				<TextInput
					label="Bonus Valid for (in days)"
					name="valid_days"
					disabled
					errors={errors && errors.valid_days}
					className="w-1/3"
					type="number"
					min="0"
					max="30"
					step="1"
					bind:value={promotion.rules.valid_days}
				/>
			</div>
			<div class="flex flex-row flex-shrink mt-4">
				<div class="flex flex-row items-center justify-center space-x-4">
					<div class="flex flex-row items-center justify-center mr-4">
						<label class="form-label" for="promo-activeTime">Publish At:</label>
						<Switch name={'promo-activeToggle'} bind:checked={activeToggle} className="ml-2" />
					</div>
					{#if activeToggle}
						<Datepicker
							selected={activeDate}
							displayFormat="DD-MM-YYYY"
							dateChosen={(date) => {
								activeDate = dayjs(date).format('YYYY-MM-DD');
							}}
							appendBtnStyle="h-10"
						/>
						<TextInput bind:value={activeTime} name={'promo-activeTime'} type="time" inputClasses="" />
					{/if}
				</div>
			</div>
			<div class="flex flex-row flex-shrink mt-4 mb-4">
				<div class="flex flex-row items-center justify-center space-x-4">
					<div class="flex flex-row items-center justify-center mr-4">
						<label class="form-label" for="promo-expireTime">Expires At:</label>
						<Switch name={'promo-expireToggle'} bind:checked={expireToggle} className="ml-2" />
					</div>
					{#if expireToggle}
						<Datepicker
							selected={expireDate}
							displayFormat="DD-MM-YYYY"
							dateChosen={(date) => {
								expireDate = dayjs(date).format('YYYY-MM-DD');
							}}
						/>
						<TextInput bind:value={expireTime} name={'promo-expireTime'} type="time" />
					{/if}
				</div>
			</div>
			<LoadingButton
				on:click={() => updatePromotion()}
				loading={sending}
				className="my-4 btn {!($ACLStore && ACLStore.granted('promotions.update'))
					? 'btn-primary-action-disabled'
					: 'btn-primary'}"
				disabled={!($ACLStore && ACLStore.granted('promotions.update'))}
			>
				{sending ? 'Updating' : 'Update'}
			</LoadingButton>
			<hr />
			<h2 class="pt-2 text-xl font-bold text-center">
				Users added to promotion - {promotion.name}
			</h2>
			{#if $PromotionUserStore.count() > 0}
				<div class="flex flex-row justify-end items-center">
					<button
						class="focus:outline-none btn-primary p-2 rounded m-1.5"
						on:click|preventDefault|stopPropagation={downloadUserList}
					>
						Export as CSV
					</button>
				</div>
				<Pagination {links} on:click={changePage} className="my-3" />
				<table>
					<tr>
						<th>Name</th>
						<th>Used</th>
						<th>Created at</th>
					</tr>
					{#each $PromotionUserStore.all() as promotionUser (promotionUser.id)}
						<tr>
							<td>{promotionUser.name}</td>
							<td>{promotionUser.used === true ? 'Yes' : 'No'}</td>
							<td>{dayjs(promotionUser.created_at).format('D MMM YYYY, HH:mm')}</td>
						</tr>
					{/each}
				</table>
			{/if}
			<hr />
			<TextInput
				label="Add new users Via CSV"
				name="promotion_user_list"
				type="file"
				className="w-full mt-4"
				required={true}
				errors={errors && errors.promotion_user_list}
				on:change={fileChange}
				accept=".csv"
			/>
			<LoadingButton
				on:click={checkBonusAmountConfirmation}
				loading={sending}
				className="my-4 btn {!($ACLStore && ACLStore.granted('promotions.update'))
					? 'btn-primary-action-disabled'
					: 'btn-primary-action'}"
				disabled={!($ACLStore && ACLStore.granted('promotions.update'))}
			>
				{sending ? (closing ? 'Upload' : 'Uploading') : 'Upload'}
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}>Cancel</button>
		</div>
	</Modal>
{/if}
